// A doctor mid-consultation often needs "what did we write last time" without
// leaving this screen for Patient Records — this card is the shortcut.
// Reuses keys.clinical.consultations (queryKeys.ts), which was added ahead of
// the deep-consultation rebuild but had no caller yet, and the same
// markdown-lite renderer ConsultationChat/DischargeSummaryChat use, so a
// past note reads exactly as it did when it was written, not as a raw text
// dump.
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText } from 'lucide-react';
import { getConsultations } from '../../api';
import { keys } from '@/lib/queryKeys';
import { renderLiteMarkdown } from '@/lib/markdownLite';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/Dialog';
import { RailCard } from '../../components/stages/ConsultStage';
import { useAuth } from '@/hooks/useAuth';
import { toast } from '@/hooks/useToast';
import { Button } from '@/components/ui/Button';
import { AmendNoteDialog, VersionTag, canAmendNote, type AmendableNote } from './NoteAmendment';

export interface ConsultationRow extends AmendableNote {
  ai_draft?: string | null;
  created_at: string;
  queue_id?: number | null;
}

const DOC_ICONS: Record<string, string> = { 'OPD Prescription': '💊', 'Consultation Notes': '📝', 'Discharge Summary': '📋' };
const SHOWN_LIMIT = 5;

export function usePatientConsultations(patientId: number | undefined) {
  return useQuery({
    queryKey: keys.clinical.consultations(patientId ?? 0),
    queryFn: () => getConsultations(patientId as number) as Promise<ConsultationRow[]>,
    enabled: !!patientId,
  });
}

/**
 * Rail card listing this patient's earlier approved consultation documents,
 * each a link that opens the full note in a popup. Only approved documents
 * count as "the record of what happened" (an abandoned draft would just be
 * noise here), and the visit currently open is excluded — that document is
 * already in the Notes tab, not "previous".
 */
export function PreviousConsultationsCard({ patientId, excludeQueueId }: {
  patientId: number | undefined;
  excludeQueueId?: number | null;
}) {
  const { data } = usePatientConsultations(patientId);
  const { user } = useAuth();
  const qc = useQueryClient();
  const [viewing, setViewing] = useState<ConsultationRow | null>(null);
  const [amending, setAmending] = useState<ConsultationRow | null>(null);
  // A superseded version (G5 amendment) is history, not a separate consultation.
  const rows = (data ?? []).filter(c => c.approved && !c.superseded_by_consultation_id && c.queue_id !== excludeQueueId);

  if (rows.length === 0) return null;

  return (
    <>
      <RailCard title="Previous consultations" icon={<FileText size={13} />}>
        <div className="flex flex-col gap-1.5">
          {rows.slice(0, SHOWN_LIMIT).map(c => (
            <button
              key={c.id}
              type="button"
              onClick={() => setViewing(c)}
              className="flex items-center gap-1.5 text-left text-[12.5px] text-ink hover:text-teal"
            >
              <span className="shrink-0">{DOC_ICONS[c.doc_type] || '📄'}</span>
              <span className="flex-1 truncate underline-offset-2 hover:underline">{c.doc_type}</span>
              <span className="shrink-0 text-[11px] text-ink-muted">
                {new Date(c.approved_at || c.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
              </span>
            </button>
          ))}
          {rows.length > SHOWN_LIMIT && (
            <div className="text-[11px] text-ink-muted">+{rows.length - SHOWN_LIMIT} more in Patient Records</div>
          )}
        </div>
      </RailCard>

      <Dialog open={!!viewing} onOpenChange={(open) => { if (!open) setViewing(null); }}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          {viewing && (
            <>
              <DialogHeader>
                <DialogTitle>{DOC_ICONS[viewing.doc_type] || '📄'} {viewing.doc_type}</DialogTitle>
                <DialogDescription>
                  {viewing.approved_at ? new Date(viewing.approved_at).toLocaleString('en-IN') : 'Not yet approved'}
                  {viewing.approved_by ? ` · ${(viewing.version ?? 1) > 1 ? 'amended' : 'approved'} by ${viewing.approved_by}` : ''}
                </DialogDescription>
                <VersionTag note={viewing} />
              </DialogHeader>
              <div className="rounded-[var(--radius)] border border-line bg-bg p-3 text-[13px] leading-relaxed text-ink">
                {renderLiteMarkdown(viewing.final_doc || viewing.ai_draft || 'No content recorded.')}
              </div>
              {canAmendNote(viewing, user) && (
                <div className="flex justify-end">
                  <Button variant="ghost" onClick={() => { setAmending(viewing); setViewing(null); }}>✎ Amend note</Button>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      <AmendNoteDialog
        note={amending}
        onClose={() => setAmending(null)}
        onAmended={(a) => {
          setAmending(null);
          toast({ title: `Amendment saved as version ${a.version ?? ''}`, variant: 'success' });
          if (patientId) void qc.invalidateQueries({ queryKey: keys.clinical.consultations(patientId) });
        }}
      />
    </>
  );
}
